import ReactSelect, { components } from 'react-select';
import { BiCheckbox, BiCheckboxSquare } from 'react-icons/bi';
import { BsDashSquare } from 'react-icons/bs';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { useTheme } from 'styled-components';
import { Button } from 'react-bootstrap';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocationFilterConfig } from './context';
import { usePrevious } from '../../helpers/hooks/usePrevious';
import { useSelector } from 'react-redux';
import type { RootState } from '../../state/store';
import type { MenuProps, OptionProps } from 'react-select';

const Option = ({ ...props }: OptionProps) => {
  const { selectProps, data, label } = props;
  const { companyStates, expandedStates, setExpandedStates } = useLocationFilterConfig();
  const optionData = data as any;

  const isStateOption = optionData.isState;
  const isChildOption = optionData.isChild;

  // Check if this option (state or location) is selected
  const isOptionChecked = useMemo(() => {
    if (!selectProps.value) return false;

    const selectedValues = selectProps.value as any;

    // If this is a child location, check:
    // 1. Is this location directly selected? OR
    // 2. Is the parent state selected?
    if (isChildOption && optionData.parentState) {
      const isLocationSelected = selectedValues.some((v: any) => v.id === optionData.id);
      const isParentStateSelected = selectedValues.some(
        (v: any) => v.isState && v.id === optionData.parentState
      );
      return isLocationSelected || isParentStateSelected;
    }

    // For state or non-hierarchical options
    return selectedValues.some((v: any) => v.id === optionData.id);
  }, [selectProps.value, optionData, isChildOption]);

  // For state options, check if all child locations are selected (full) or some (partial)
  const { isPartiallySelected, isFullySelected } = useMemo(() => {
    if (!isStateOption) return { isPartiallySelected: false, isFullySelected: false };

    const stateData = companyStates.find((s: any) => s.id === optionData.id);
    if (!stateData) return { isPartiallySelected: false, isFullySelected: false };

    const selectedValues = selectProps.value as any;

    // Check if this state itself is selected
    const isStateDirectlySelected = selectedValues?.some(
      (v: any) => v.isState && v.id === optionData.id
    );

    // If state is directly selected, it's fully selected
    if (isStateDirectlySelected) {
      return { isPartiallySelected: false, isFullySelected: true };
    }

    // Otherwise, check how many individual locations are selected
    const selectedLocationIds =
      selectedValues?.filter((v: any) => !v.isState).map((v: any) => v.id) || [];
    const stateLocationIds = stateData.locations.map((loc: any) => loc.id);
    const selectedCount = stateLocationIds.filter((id: any) =>
      selectedLocationIds.includes(id)
    ).length;

    return {
      isPartiallySelected: selectedCount > 0 && selectedCount < stateLocationIds.length,
      isFullySelected: selectedCount === stateLocationIds.length && selectedCount > 0
    };
  }, [isStateOption, optionData, selectProps.value, companyStates]);

  const toggleStateExpansion = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedStates((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(optionData.id)) {
        newSet.delete(optionData.id);
      } else {
        newSet.add(optionData.id);
      }
      return newSet;
    });
  };

  // Handle clicking a child when parent is selected - need to "break apart" parent selection
  const handleChildClick = (e: any) => {
    if (isChildOption && optionData.parentState) {
      const currentValues = (selectProps.value as any) || [];
      const parentIsSelected = currentValues.some(
        (v: any) => v.isState && v.id === optionData.parentState
      );

      if (parentIsSelected) {
        // Parent is selected, need to break it apart
        e.preventDefault();
        e.stopPropagation();

        const parentState = companyStates.find((s: any) => s.id === optionData.parentState);
        if (!parentState) return;

        // Remove parent state and add all siblings except this one
        const newValues = currentValues.filter((v: any) => v.id !== optionData.parentState);

        parentState.locations.forEach((loc: any) => {
          if (loc.id !== optionData.id) {
            // Add all other locations from this state
            newValues.push({
              id: loc.id,
              label: loc.name,
              value: loc.id,
              isState: false
            });
          }
        });

        // Call onChange directly
        (selectProps as any).onChange(newValues, { action: 'deselect-option' });
        return;
      }
    }

    // If not a special case, call the original onClick
    if (props.innerProps.onClick) {
      props.innerProps.onClick(e);
    }
  };

  return (
    <components.Option
      {...props}
      innerProps={{ ...props.innerProps, onClick: handleChildClick } as any}
    >
      <div
        className={`hierarchical-option ${
          isChildOption ? 'child-option' : isStateOption ? 'parent-option' : ''
        }`}
      >
        {isChildOption && <span className="child-indent"></span>}
        {isStateOption && isPartiallySelected ? (
          <BsDashSquare fontSize="17.5px" className="partial-checkbox" />
        ) : isOptionChecked || isFullySelected ? (
          <BiCheckboxSquare fontSize="30px" />
        ) : (
          <BiCheckbox fontSize="30px" />
        )}
        <span className="option-label">{label}</span>
        {isStateOption && (
          <span className="expand-arrow" onClick={toggleStateExpansion}>
            {expandedStates.has(optionData.id) ? (
              <FaChevronDown size={12} />
            ) : (
              <FaChevronRight size={12} />
            )}
          </span>
        )}
      </div>
    </components.Option>
  );
};

const Menu = ({ children, ...props }: MenuProps) => {
  const { selectProps } = props;

  const {
    selectedLocations,
    companyLocations,
    setClearSelection,
    handleSubmit,
    disableSubmit,
    toggleSelectAll,
    setToggleSelectAll,
    setPartialSelect
  } = useLocationFilterConfig();

  const { locations: locationsFilter } = useSelector((state: RootState) => state.filters);

  const handleClearSelection = () => {
    setClearSelection(true);
  };

  const handleSelectAllOptions = () => {
    setPartialSelect(false);
    setToggleSelectAll((i) => !i);
  };

  const disabledApplyButton = useMemo(() => {
    const locationDiff = selectedLocations.filter(
      (i: any) => !(locationsFilter as any).includes(i.id)
    );
    return !Boolean(locationDiff?.length) && selectedLocations?.length == locationsFilter?.length;
  }, [selectedLocations, locationsFilter]);

  useEffect(() => {
    if (selectedLocations.length === companyLocations.length) {
      setToggleSelectAll(true);
    } else if (selectedLocations.length == 0) {
      setPartialSelect(false);
      setToggleSelectAll(false);
    } else {
      setPartialSelect(true);
      setToggleSelectAll(false);
    }
  }, [selectedLocations, companyLocations]);

  return (
    <components.Menu {...props}>
      <div
        className={
          (selectProps as any)?.isInFilteringState ? 'hide-select-all' : 'select_all_option'
        }
        onClick={handleSelectAllOptions}
      >
        <span style={{ margin: '10px 0 10px 12px' }}>
          {toggleSelectAll ? <BiCheckboxSquare fontSize="30px" /> : <BiCheckbox fontSize="30px" />}
        </span>
        Select All
      </div>
      {children}
      <div className="dropdown-actions">
        <Button className="btn cancel-btn" onClick={handleClearSelection} disabled={false}>
          Clear
        </Button>
        <Button
          className="btn submit-btn"
          onClick={handleSubmit}
          disabled={disableSubmit || disabledApplyButton}
        >
          Submit
        </Button>
      </div>
    </components.Menu>
  );
};

const MultiSelectMenu = () => {
  const theme = useTheme();
  const multiSelectRef = useRef<any>(null);
  const [isFiltering, setIsFiltering] = useState(false);
  const isInitialLoad = useRef(true);

  const {
    companyLocations,
    companyStates,
    clearSelection,
    appliedFilters,
    handleHierarchicalSelect,
    setClearSelection,
    toggleSelectAll,
    isMenuOpen,
    partialSelect,
    selectedLocations,
    isStateFilteringAvailable,
    expandedStates
  } = useLocationFilterConfig();

  // Initialize values when menu opens
  useEffect(() => {
    if (!isMenuOpen) {
      isInitialLoad.current = true;
      return;
    }

    if (isInitialLoad.current) {
      isInitialLoad.current = false;

      if (isStateFilteringAvailable) {
        // For hierarchical view, we need to show both selected states and locations
        const selectedOptions: any[] = [];

        // Add fully selected states
        companyStates.forEach((state: any) => {
          const allLocationIds = state.locations.map((loc: any) => loc.id);
          const allSelected = allLocationIds.every((id: any) => appliedFilters.includes(id));
          if (allSelected && allLocationIds.length > 0) {
            selectedOptions.push({
              id: state.id,
              label: `${state.name} (${state.location_count})`,
              value: state.id,
              isState: true,
              locations: state.locations
            });
          }
        });

        // Add individual locations that aren't part of fully selected states
        const fullySelectedStateIds = selectedOptions.map((s) => s.id);
        companyLocations
          .filter((loc: any) => appliedFilters.includes(loc.id))
          .filter((loc: any) => {
            // Only include if its state isn't fully selected
            const parentState = companyStates.find((s: any) =>
              s.locations.some((l: any) => l.id === loc.id)
            );
            return !parentState || !fullySelectedStateIds.includes(parentState.id);
          })
          .forEach((loc: any) => {
            selectedOptions.push({
              id: loc.id,
              label: loc.name,
              value: loc.id,
              isState: false
            });
          });

        multiSelectRef.current.setValue(selectedOptions);
      } else {
        // Flat list for non-supported countries
        multiSelectRef.current.setValue(
          companyLocations
            .filter((d: any) => appliedFilters.includes(d.id))
            .map((v: any) => ({
              id: v.id,
              label: v.name,
              value: v.id,
              isState: false
            }))
        );
      }
    }
  }, [isMenuOpen, appliedFilters, companyStates, companyLocations, isStateFilteringAvailable]);

  useEffect(() => {
    if (clearSelection) {
      if (multiSelectRef.current) {
        multiSelectRef.current.clearValue();
      }
    }
    setClearSelection(false);
  }, [clearSelection, setClearSelection]);

  const prevToggleState = usePrevious(toggleSelectAll);

  useEffect(() => {
    if (!isMenuOpen || isInitialLoad.current) return;

    // Only run this effect when toggleSelectAll changes (user clicks Select All button)
    if (prevToggleState === undefined) return;

    if (toggleSelectAll) {
      if (isStateFilteringAvailable) {
        // Select all states (which includes all locations)
        multiSelectRef.current.setValue(
          companyStates.map((v: any) => ({
            id: v.id,
            label: `${v.name} (${v.location_count})`,
            value: v.id,
            isState: true,
            locations: v.locations
          }))
        );
      } else {
        // Select all locations directly
        multiSelectRef.current.setValue(
          companyLocations.map((v: any) => ({
            id: v.id,
            label: v.name,
            value: v.id,
            isState: false
          }))
        );
      }
    } else {
      if (partialSelect) {
        // Restore previous selection
        multiSelectRef.current.setValue(
          selectedLocations.map((v: any) => ({
            id: v.id,
            label: v.name,
            value: v.id,
            isState: false
          }))
        );
      } else {
        multiSelectRef.current.setValue([]);
      }
    }
  }, [toggleSelectAll]);

  const options = useMemo(() => {
    if (isStateFilteringAvailable) {
      // Hierarchical structure
      const hierarchicalOptions: any[] = [];

      companyStates.forEach((state) => {
        // Add state as parent option
        hierarchicalOptions.push({
          id: state.id,
          label: `${state.name} (${state.location_count})`,
          value: state.id,
          isState: true,
          locations: state.locations
        });

        // Add locations as child options if state is expanded OR user is filtering
        if (expandedStates.has(state.id) || isFiltering) {
          state.locations.forEach((location: any) => {
            hierarchicalOptions.push({
              id: location.id,
              label: location.name,
              value: location.id,
              isState: false,
              parentState: state.id,
              isChild: true
            });
          });
        }
      });

      return hierarchicalOptions;
    } else {
      // Flat list for non-supported countries
      return companyLocations.map((v) => ({
        id: v.id,
        label: v.name,
        value: v.id,
        isState: false
      }));
    }
  }, [companyLocations, companyStates, isStateFilteringAvailable, expandedStates, isFiltering]);

  const handleFilterOptions = (input: string) => {
    if (input) {
      setIsFiltering(true);
    } else {
      setIsFiltering(false);
    }
  };

  return (
    <ReactSelect
      styles={{
        control: (baseStyles) => ({
          ...baseStyles,
          background: theme.dropdown.background_color
        }),
        valueContainer: (baseStyles) => ({
          ...baseStyles,
          color: theme.widget.text
        }),
        input: (baseStyles) => ({
          ...baseStyles,
          color: theme.text
        }),
        menu: (baseStyles) => ({
          ...baseStyles,
          color: theme.dropdown.color,
          background: theme.dropdown.background_color,
          ':hover': {
            color: theme.dropdown.color,
            background: theme.dropdown.background_color
          }
        }),
        option: (baseStyles) => ({
          ...baseStyles,
          background: theme.dropdown.background_color,
          ':hover': {
            color: theme.dropdown.color,
            background: theme.dropdown.hover
          }
        })
      }}
      classNamePrefix="location-select"
      components={{
        Option,
        Menu,
        IndicatorsContainer: () => <></>
      }}
      options={options}
      isMulti
      onChange={handleHierarchicalSelect as any}
      onInputChange={handleFilterOptions}
      {...({ isInFilteringState: isFiltering } as any)}
      noOptionsMessage={() => 'No Locations Available'}
      ref={multiSelectRef}
      hideSelectedOptions={false}
      closeMenuOnSelect={false}
      menuIsOpen
      controlShouldRenderValue={false}
      backspaceRemovesValue={false}
      isClearable={false}
      placeholder={`Search ${isStateFilteringAvailable ? 'State/Location' : 'Location'}...`}
    />
  );
};

export default MultiSelectMenu;
